import { Download } from 'lucide-react';
import { useEffect, useState } from 'react';
import { type Components } from 'react-markdown';
import { z } from 'zod';

import { Markdown, MarkdownAnchor } from '@apps/web/components/ui/markdown';
import { safeMarkdownUrl } from '@apps/web/lib/safe_markdown';
import {
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import {
    type AdminArtifactRendererProps,
    toRecord,
} from '@apps/web/routes/_app/admin/agent/artifacts/shared';

export const MAX_FILE_ARTIFACT_BYTES = 10 * 1024 * 1024;

const filePayloadSchema = z.object({
    filename: z.string().min(1),
    content_type: z.string().min(1),
    size_bytes: z.number().int().nonnegative().max(MAX_FILE_ARTIFACT_BYTES),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    content: z.string().optional(),
    base64_data: z.string().optional(),
});

const markdownPayloadSchema = filePayloadSchema.extend({
    content: z.string(),
});

export type AdminFilePayload = z.infer<typeof filePayloadSchema>;

export function parseAdminFilePayload(
    payload: unknown,
): AdminFilePayload | null {
    const parsed = filePayloadSchema.safeParse(payload);
    return parsed.success ? parsed.data : null;
}

export function AdminMarkdownArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const parsed = markdownPayloadSchema.safeParse(toRecord(artifact.payload));
    if (!parsed.success) {
        return <InvalidFileArtifact artifact={artifact} />;
    }

    const payload = parsed.data;
    return (
        <ArtifactFrame
            artifact={artifact}
            title={artifact.title}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <div
                data-testid="admin-file-markdown"
                className="text-foreground min-w-0 break-words"
            >
                <Markdown
                    skipHtml={true}
                    urlTransform={safeMarkdownUrl}
                    components={SAFE_MARKDOWN_COMPONENTS}
                    className="max-w-none"
                >
                    {payload.content}
                </Markdown>
            </div>
        </ArtifactFrame>
    );
}

export function AdminFileDownloadArtifact({
    artifact,
}: AdminArtifactRendererProps) {
    const parsed = filePayloadSchema.safeParse(toRecord(artifact.payload));
    if (!parsed.success) {
        return <InvalidFileArtifact artifact={artifact} />;
    }

    return (
        <ArtifactFrame
            artifact={artifact}
            title={artifact.title}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <FileDownloadCard payload={parsed.data} />
        </ArtifactFrame>
    );
}

export function FileDownloadCard({
    payload,
}: {
    readonly payload: AdminFilePayload;
}) {
    const downloadHref = useFileDownloadUrl(payload);
    const hasDownloadContent =
        payload.base64_data !== undefined || payload.content !== undefined;
    if (!hasDownloadContent) {
        return (
            <p className="text-muted-foreground text-xs">
                This file artifact has no downloadable content.
            </p>
        );
    }
    if (!downloadHref) {
        return (
            <p className="text-muted-foreground text-xs">Preparing download…</p>
        );
    }

    return (
        <div
            data-testid="admin-file-download-card"
            className="border-border/60 bg-muted/30 flex min-w-0 flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2.5"
        >
            <dl className="grid min-w-0 gap-1 text-xs">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                    <dt className="text-muted-foreground">Type</dt>
                    <dd className="text-foreground break-all">
                        {payload.content_type}
                    </dd>
                </div>
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                    <dt className="text-muted-foreground">Size</dt>
                    <dd className="text-foreground">
                        {formatFileSize(payload.size_bytes)}
                    </dd>
                </div>
            </dl>
            <a
                className="bg-foreground text-background inline-flex w-fit shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-opacity hover:opacity-85"
                data-testid="admin-file-artifact-download"
                download={payload.filename}
                href={downloadHref}
            >
                <Download className="size-3.5" />
                Download file
            </a>
        </div>
    );
}

function InvalidFileArtifact({
    artifact,
}: {
    readonly artifact: AdminArtifactRendererProps['artifact'];
}) {
    return (
        <ArtifactFrame
            artifact={artifact}
            status={
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
            }
        >
            <ArtifactEvidence artifact={artifact} />
            <p className="text-muted-foreground text-xs">
                This file artifact is missing safe download metadata.
            </p>
        </ArtifactFrame>
    );
}

const SAFE_MARKDOWN_COMPONENTS: Components = {
    a: ({ node, href, children, ...props }) => {
        const safeHref = href ? safeMarkdownUrl(href) : '';
        if (!safeHref) {
            return <span {...props}>{children}</span>;
        }
        return (
            <MarkdownAnchor href={safeHref} {...props}>
                {children}
            </MarkdownAnchor>
        );
    },
    img: ({ node, alt }) => (
        <span className="text-muted-foreground text-xs">
            [{alt || 'image'} omitted]
        </span>
    ),
};

function useFileDownloadUrl(payload: AdminFilePayload): string | null {
    const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

    useEffect(() => {
        let blob: Blob;
        try {
            if (payload.base64_data !== undefined) {
                const binary = atob(payload.base64_data);
                const bytes = Uint8Array.from(binary, (character) =>
                    character.charCodeAt(0),
                );
                blob = new Blob([bytes], { type: payload.content_type });
            } else if (payload.content !== undefined) {
                blob = new Blob([payload.content], {
                    type: payload.content_type,
                });
            } else {
                setDownloadUrl(null);
                return;
            }
        } catch {
            setDownloadUrl(null);
            return;
        }

        const url = URL.createObjectURL(blob);
        setDownloadUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [payload.base64_data, payload.content, payload.content_type]);

    return downloadUrl;
}

function formatFileSize(sizeBytes: number): string {
    if (sizeBytes < 1024) {
        return `${sizeBytes} B`;
    }
    if (sizeBytes < 1024 * 1024) {
        return `${(sizeBytes / 1024).toFixed(1)} KB`;
    }
    return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}
